import { useState, useEffect } from 'react';
import { useAuth, authHeaders } from '@/context/AuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { User, MapPin, Lock, Package, ChevronRight, LogOut, Mail, ShieldCheck, Database } from 'lucide-react';
import { formatAuPhone, PHONE_PLACEHOLDER } from '@/utils/phone';
import AccountSidebar from '@/components/AccountSidebar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'orders', label: 'My Orders', icon: Package },
  { id: 'profile', label: 'Personal Details', icon: User },
  { id: 'address', label: 'Address', icon: MapPin },
  { id: 'security', label: 'Password', icon: Lock },
];

export default function AccountPage() {
  const { user, logout, refreshUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'orders';
  const [tab, setTab] = useState(initialTab);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [saving, setSaving] = useState(false);

  const [profile, setProfile] = useState({ name: '', phone: '' });
  const [address, setAddress] = useState({ line1: '', line2: '', city: '', state: '', postcode: '', country: 'Australia' });
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm: '' });

  useEffect(() => {
    if (!authLoading && !user) navigate('/login', { replace: true });
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      setProfile({ name: user.name || '', phone: formatAuPhone(user.phone || '') });
      setAddress(user.address || { line1: '', line2: '', city: '', state: '', postcode: '', country: 'Australia' });
    }
  }, [user]);

  useEffect(() => {
    if (user && tab === 'orders') fetchOrders();
  }, [user, tab]);

  const fetchOrders = async () => {
    setLoadingOrders(true);
    try {
      const res = await fetch(`${API}/orders/my`, { headers: authHeaders() });
      if (res.ok) setOrders(await res.json());
    } catch { /* ignore */ }
    setLoadingOrders(false);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/auth/profile`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify(profile) });
      if (res.ok) { await refreshUser(); toast.success('Profile updated'); } else { const e = await res.json(); toast.error(e.detail); }
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const saveAddress = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/auth/address`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify({ address }) });
      if (res.ok) { await refreshUser(); toast.success('Address updated'); } else { const e = await res.json(); toast.error(e.detail); }
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const changePassword = async () => {
    if (passwords.new_password !== passwords.confirm) { toast.error('Passwords do not match'); return; }
    if (passwords.new_password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/auth/password`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify({ current_password: passwords.current_password, new_password: passwords.new_password }) });
      if (res.ok) { toast.success('Password changed'); setPasswords({ current_password: '', new_password: '', confirm: '' }); } else { const e = await res.json(); toast.error(e.detail); }
    } catch { toast.error('Failed to change password'); }
    setSaving(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
    toast.success('Logged out');
  };

  const statusColor = (s) => {
    const map = { order_received: 'bg-blue-100 text-blue-700', in_production: 'bg-amber-100 text-amber-700', shipped: 'bg-purple-100 text-purple-700', delivered: 'bg-green-100 text-green-700', cancelled: 'bg-red-100 text-red-700', refunded: 'bg-gray-100 text-gray-600' };
    return map[s] || 'bg-gray-100 text-gray-600';
  };
  const statusLabel = (s) => s?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '';

  if (authLoading || !user) return <div className="pt-20 sm:pt-[122px] min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-bush" /></div>;

  return (
    <div className="pt-20 sm:pt-[122px] min-h-screen bg-cream" data-testid="account-page">
      <section className="bg-white py-10 sm:py-16 border-b border-cream-dark">
        <div className="max-w-[1632px] mx-auto px-5 sm:px-8 lg:px-12">
          <h1 className="text-4xl sm:text-5xl font-display font-semibold text-[#1A1A1A] mb-4 tracking-tight">My Account</h1>
          <p className="text-base sm:text-lg text-gray-500 max-w-2xl font-body">Manage your orders, personal details, and account settings.</p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-6 sm:py-12">
      <div className="flex flex-col sm:flex-row gap-6">
        <AccountSidebar activeTab={tab} onTabChange={setTab} />

        {/* Content */}
        <div className="flex-1 bg-white rounded-xl shadow-card p-5 sm:p-6">
          {/* ORDERS TAB */}
          {tab === 'orders' && (
            <div>
              <h2 className="text-lg font-serif font-bold mb-4">My Orders</h2>
              {loadingOrders ? (
                <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-t-2 border-bush" /></div>
              ) : orders.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Package className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>No orders yet</p>
                  <Link to="/products" className="text-bush text-sm hover:underline mt-2 inline-block">Start shopping</Link>
                </div>
              ) : (
                <div className="space-y-5">
                  {orders.map(o => (
                    <Link key={o.order_id} to={`/order/${o.order_id}`}
                      className="block w-full p-4 rounded-lg border-2 border-gray-200 hover:border-bush/40 transition-colors group"
                      data-testid={`order-${o.order_id}`}>
                      {o.unread_count > 0 && (
                        <div className="flex items-center gap-1.5 mb-2">
                          <span className="flex items-center gap-1 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                            <Mail className="w-3 h-3" />{o.unread_count} unread message{o.unread_count > 1 ? 's' : ''}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm text-gray-500">{new Date(o.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-bush transition-colors flex-shrink-0" />
                      </div>
                      <p className="text-sm"><span className="text-gray-400">Order ID:</span> <span className="font-semibold">{o.order_id}</span></p>
                      <p className="text-sm font-semibold mt-1">${o.total?.toFixed(2)}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-sm text-gray-400">Status:</span>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColor(o.status)}`}>{statusLabel(o.status)}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* PROFILE TAB */}
          {tab === 'profile' && (
            <div>
              <h2 className="text-lg font-serif font-bold mb-4">Personal Details</h2>
              <div className="space-y-4 max-w-md">
                <div><Label className="text-sm font-medium">Full Name</Label><Input value={profile.name} onChange={e => setProfile({...profile, name: e.target.value})} className="mt-1 h-11" data-testid="profile-name" /></div>
                <div><Label className="text-sm font-medium">Email</Label><Input value={user.email} disabled className="mt-1 h-11 bg-gray-50" /><p className="text-xs text-gray-400 mt-1">Contact support to change your email</p></div>
                <div><Label className="text-sm font-medium">Phone</Label><Input value={profile.phone} onChange={e => setProfile({...profile, phone: formatAuPhone(e.target.value)})} placeholder={PHONE_PLACEHOLDER} className="mt-1 h-11" data-testid="profile-phone" /></div>
                <Button onClick={saveProfile} disabled={saving} className="bg-bush hover:bg-bush/90 text-white" data-testid="save-profile-btn">{saving ? 'Saving...' : 'Save Changes'}</Button>
              </div>
            </div>
          )}

          {/* ADDRESS TAB */}
          {tab === 'address' && (
            <div>
              <h2 className="text-lg font-serif font-bold mb-4">Shipping Address</h2>
              <div className="space-y-4 max-w-md">
                <div><Label className="text-sm font-medium">Address Line 1</Label><Input value={address.line1} onChange={e => setAddress({...address, line1: e.target.value})} className="mt-1 h-11" data-testid="address-line1" /></div>
                <div><Label className="text-sm font-medium">Address Line 2</Label><Input value={address.line2} onChange={e => setAddress({...address, line2: e.target.value})} className="mt-1 h-11" data-testid="address-line2" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-sm font-medium">City</Label><Input value={address.city} onChange={e => setAddress({...address, city: e.target.value})} className="mt-1 h-11" data-testid="address-city" /></div>
                  <div><Label className="text-sm font-medium">State</Label><Input value={address.state} onChange={e => setAddress({...address, state: e.target.value})} className="mt-1 h-11" data-testid="address-state" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-sm font-medium">Postcode</Label><Input value={address.postcode} onChange={e => setAddress({...address, postcode: e.target.value})} className="mt-1 h-11" data-testid="address-postcode" /></div>
                  <div><Label className="text-sm font-medium">Country</Label><Input value={address.country} onChange={e => setAddress({...address, country: e.target.value})} className="mt-1 h-11" data-testid="address-country" /></div>
                </div>
                <Button onClick={saveAddress} disabled={saving} className="bg-bush hover:bg-bush/90 text-white" data-testid="save-address-btn">{saving ? 'Saving...' : 'Save Address'}</Button>
              </div>
            </div>
          )}

          {/* SECURITY TAB */}
          {tab === 'security' && (
            <div>
              <h2 className="text-lg font-serif font-bold mb-4">Change Password</h2>
              {user.auth_provider === 'google' && !user.password_hash ? (
                <p className="text-sm text-gray-500">Your account uses Google sign-in. You can set a password below to also enable email/password login.</p>
              ) : null}
              <div className="space-y-4 max-w-md mt-4">
                {user.auth_provider !== 'google' && (
                  <div><Label className="text-sm font-medium">Current Password</Label><Input type="password" value={passwords.current_password} onChange={e => setPasswords({...passwords, current_password: e.target.value})} className="mt-1 h-11" data-testid="current-password" /></div>
                )}
                <div><Label className="text-sm font-medium">New Password</Label><Input type="password" value={passwords.new_password} onChange={e => setPasswords({...passwords, new_password: e.target.value})} placeholder="Min 6 characters" className="mt-1 h-11" data-testid="new-password" /></div>
                <div><Label className="text-sm font-medium">Confirm New Password</Label><Input type="password" value={passwords.confirm} onChange={e => setPasswords({...passwords, confirm: e.target.value})} className="mt-1 h-11" data-testid="confirm-new-password" /></div>
                <Button onClick={changePassword} disabled={saving} className="bg-bush hover:bg-bush/90 text-white" data-testid="change-password-btn">{saving ? 'Changing...' : 'Change Password'}</Button>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
